import {
  Autocomplete,
  BulkInput,
  InputField,
  TimeInput,
} from 'components/common';
import { TStateValue } from 'components/core/FilterSelects/utils';
import {
  applyOverride,
  formatDate,
  retrieveDate,
} from 'components/core/FilterSelects/utils';
import { useContext } from 'react';
import { ViewContext } from 'utils/context';
import { TFilterOptions } from 'utils/context/FilterContext/types';
import { getKeyByValue } from 'utils/context/FilterContext/utils';
import DK from 'utils/dictionaries/filter_dictionary';
import { usePermissions } from 'utils/hooks';

import {
  Flex,
  Icon,
  IconButton,
  Select,
  SelectItem,
  TextField,
} from '@project44/manifest-ds';
import { DateRangePicker } from '@project44/manifest-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';
import { ShipmentIdentifierFilter } from '@project44-models/shipment-list';

const styles = {
  cell: {
    backgroundColor: 'neutral.50',
    width: '30%',
  },
  flex: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
};

export function SelectCriteria({
  isOrder,
  criteria,
  handleSelect,
  index,
  options,
  startVariant,
}: {
  isOrder: boolean;
  criteria: string;
  handleSelect: (value: string | string[], index: number, idx: number) => void;
  index: number;
  options: string[];
  startVariant?: string;
}) {
  const { t } = useTranslation();
  const keyVariant = isOrder
    ? 'filter.suggestions.orderAttributeKey'
    : 'filter.suggestions.attributeKey';
  if (startVariant === 'Autocomplete') {
    return (
      <Autocomplete
        onChange={(value) => handleSelect(value[0], index, 0)}
        searchPlaceholder={t('core.autocompleteSearchPlaceholder')}
        suggestKey={keyVariant}
        suggestValue={keyVariant}
        value={[criteria]}
      />
    );
  }
  return (
    <Select
      aria-label={t('core.selectCriteria')}
      selectedKey={criteria}
      onSelectionChange={(value) => handleSelect(value as string, index, 0)}
      children={options.filter(Boolean).map((opt) => (
        <SelectItem key={opt} aria-label={opt} children={t(opt)} />
      ))}
    />
  );
}

export function SelectOperator({
  handleSelect,
  index,
  isDisabled,
  operator,
  options,
}: {
  handleSelect: (value: string | string[], index: number, idx: number) => void;
  index: number;
  isDisabled: boolean;
  operator: string;
  options: string[];
}) {
  const { t } = useTranslation();
  return (
    <Select
      aria-label={t('core.selectOperator')}
      isDisabled={isDisabled}
      selectedKey={operator}
      onSelectionChange={(value) => handleSelect(value as string, index, 1)}
      children={options.filter(Boolean).map((opt) => (
        <SelectItem key={opt} aria-label={opt} children={t(opt)} />
      ))}
    />
  );
}

export function SelectTerms({
  isOrder,
  criteria,
  handleSelect,
  index,
  isDisabled,
  options,
  startVariant,
  terms,
  variant,
}: {
  isOrder: boolean;
  criteria: string;
  handleSelect: (value: string[], index: number, idx: number) => void;
  index: number;
  isDisabled: boolean;
  options: string[];
  startVariant?: string;
  terms: string[];
  variant: string;
}) {
  const { t } = useTranslation();
  const valueVariant = isOrder
    ? 'filter.suggestions.orderAttributeValue'
    : 'filter.suggestions.attributeValue';
  switch (variant) {
    case 'Autocomplete':
      return (
        <Autocomplete
          value={terms}
          isDisabled={isDisabled}
          multiple
          onChange={(value) => handleSelect(value, index, 2)}
          searchPlaceholder="Search..."
          suggestValue={t(criteria)}
          suggestKey={startVariant ? valueVariant : criteria}
        />
      );
    case 'DatePicker':
      return (
        <DateRangePicker
          isDisabled={isDisabled}
          value={retrieveDate(terms)}
          onChange={({ startDate, endDate }) => {
            const start = new Date(startDate.getTime());
            const end = new Date(endDate.getTime());
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            const value = [start, end].map(formatDate);
            handleSelect(value, index, 2);
          }}
        />
      );
    case 'Input':
      return (
        <TextField
          aria-label="Input Terms"
          isDisabled={isDisabled}
          value={terms[0]}
          onChange={(value) => handleSelect([value], index, 2)}
        />
      );
    case 'InputField':
      return (
        <InputField
          aria-label="Input multiple terms"
          isDisabled={isDisabled}
          values={terms}
          onChange={(value) => handleSelect(value, index, 2)}
        />
      );
    case 'TimeInput':
      return (
        <TimeInput
          isDisabled={isDisabled}
          value={terms}
          onChange={(value) => handleSelect(value, index, 2)}
        />
      );
    case 'BulkInput':
      return (
        <BulkInput
          identifierType={
            getKeyByValue(DK, criteria) as ShipmentIdentifierFilter['type']
          }
          onChange={(nextTags) => handleSelect(nextTags, index, 2)}
          initialValue={terms.filter(Boolean)}
          disabled={isDisabled}
        />
      );
    case 'Select':
    default:
      return (
        <Select
          aria-label={t('core.selectTerms')}
          isDisabled={isDisabled}
          selectedKey={terms[0]}
          onSelectionChange={(value) =>
            handleSelect([value] as string[], index, 2)
          }
          children={options.filter(Boolean).map((opt) => (
            <SelectItem key={opt} aria-label={opt} children={t(opt)} />
          ))}
        />
      );
  }
}

export function FilterSelectRow({
  criteriaOptions,
  handleClear,
  handleSelect,
  index,
  options,
  values,
  startVariant,
}: {
  criteriaOptions: string[];
  handleClear: (index: number) => void;
  handleSelect: (value: string | string[], index: number, idx: number) => void;
  index: number;
  options: TFilterOptions;
  values: TStateValue;
  startVariant?: string;
}) {
  const {
    flags: { featureExc1365 },
  } = usePermissions();

  const { viewType } = useContext(ViewContext);
  const isOrder = viewType === 'order';
  const [criteria, operator, terms] = values;

  let optIndex = options.findIndex((opt) => opt.option[0] === criteria);
  optIndex = startVariant ? 0 : optIndex;

  const criteriaOpts = [...criteriaOptions];
  if (criteria) {
    criteriaOpts.splice(optIndex, 0, criteria);
  }

  const current = options[optIndex] || {
    variant: 'Select',
    option: ['', [''], ['']],
  };

  return (
    <Flex css={styles.flex}>
      <Flex css={styles.cell}>
        <SelectCriteria
          isOrder={isOrder}
          criteria={criteria}
          handleSelect={handleSelect}
          index={index}
          options={criteriaOpts}
          startVariant={startVariant}
        />
      </Flex>
      <Flex css={styles.cell}>
        <SelectOperator
          handleSelect={handleSelect}
          index={index}
          isDisabled={!criteria}
          operator={operator}
          options={current.option[1]}
        />
      </Flex>
      <Flex css={styles.cell}>
        <SelectTerms
          isOrder={isOrder}
          criteria={criteria}
          handleSelect={handleSelect}
          index={index}
          isDisabled={!operator}
          options={current.option[2]}
          startVariant={startVariant}
          terms={terms}
          variant={
            current.variant !== 'BulkInput'
              ? applyOverride(operator, featureExc1365) || current.variant
              : current.variant
          }
        />
      </Flex>
      <IconButton
        aria-label="Remove Criteria"
        children={<Icon icon="close" css={{ color: '$text-tertiary' }} />}
        isDisabled={!criteria && index === 0}
        onPress={() => handleClear(index)}
        variant="tertiary"
      />
    </Flex>
  );
}
